// ──────────────────────────────────────────────────────
// TrustBadge — customer-facing "منتج أصلي 100%" badge.
//
// Phase 1 is catalog-global: every product shows the badge. The badge is
// deliberately premium-looking (subtle emerald gradient + lock glyph) and
// clickable — tapping it opens TrustModal with the supplier/authenticity
// documents. Kept compact so it doesn't dominate the card layout.
//
// Accessibility / interaction polish:
//   - `title` attribute → native desktop tooltip "اضغط لعرض إثبات الأصالة"
//   - `aria-label` (full intent) + `aria-haspopup="dialog"` so screen
//     readers announce both the claim and the action.
//   - Focus-visible ring for keyboard users; touch/pointer left alone so
//     mobile tap stays clean and doesn't show a sticky focus ring.
//   - Subtle hover: shadow rise + lock wiggle is intentionally absent;
//     the only motion is a very small scale-down on active press so the
//     interaction feels premium, not noisy.
// ──────────────────────────────────────────────────────
'use client';

import { useState } from 'react';
import { TrustModal } from './TrustModal';

const TOOLTIP_TEXT = 'اضغط لعرض شهادة الأصالة';
const ARIA_LABEL = 'منتج أصلي 100% — اضغط لعرض شهادة الأصالة';

export function TrustBadge({ compact = false }: { compact?: boolean }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
                title={TOOLTIP_TEXT}
                aria-label={ARIA_LABEL}
                aria-haspopup="dialog"
                aria-expanded={open}
                className={
                    'inline-flex items-center gap-1.5 rounded-full border border-emerald-200 '
                    + 'bg-gradient-to-r from-emerald-50 to-emerald-100/70 '
                    + 'text-emerald-800 shadow-sm transition '
                    + 'hover:shadow-md hover:border-emerald-300 active:scale-[0.98] '
                    + 'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1 '
                    + (compact
                        ? 'px-2 py-0.5 text-[10px] font-semibold leading-tight'
                        : 'px-3 py-1 text-xs font-semibold leading-tight')
                }
            >
                <span aria-hidden>🔒</span>
                <span>منتج أصلي 100%</span>
            </button>
            {open && <TrustModal onClose={() => setOpen(false)} />}
        </>
    );
}
